import { Splide, Options, SlideComponent } from "@splidejs/splide";
import { IInitialisable } from "../../interfaces/IInitialisable";

export class Carousel implements IInitialisable {
	private splideConstructorHelper = Splide;
	private windowHelper = window;
	private documentHelper = document;

	// tested
	private get defaultOptions() {
		return {
			mediaQuery: "min",
			autoplay: this.windowHelper.navigator.userAgent.includes("HSTRegression") === false,
			rewind: true,
			pauseOnHover: false,
			pauseOnFocus: false,
			type: "loop",
			breakpoints: {
				768: {
					destroy: true,
				},
				480: {
					perPage: 1,
				},
			},
		} as Partial<Options>;
	}

	// tested
	public Init() {
		this.wrapCollectionInCarouselMarkup('[data-cmpt*="collection--"][data-cmpt-carousel].cmpt').forEach((collection) => {
			collection.querySelectorAll<HTMLElement>("[tabindex]").forEach((x) => this.storeOriginalTabIndex(x));

			const carouselType = collection.getAttribute("data-cmpt-carousel") ?? "";
			const carouselOptions = this.getCarouselOptions(carouselType);
			const splide = new this.splideConstructorHelper(collection.querySelector(".splide") as HTMLElement, carouselOptions);
			splide.mount({ HandleRemountExtension: this.handleRemountExtension.bind(this) });
		});
	}

	// tested
	private wrapCollectionInCarouselMarkup(collectionsSelector: string) {
		const collections = Array.from(this.documentHelper.querySelectorAll<HTMLElement>(collectionsSelector));
		collections.forEach((collection) => {
			const firstChildOfCollection = collection.children.item(0)?.children.item(0) as HTMLElement;
			const itemsHtml = this.getCollectionItemsHtml(Array.from(firstChildOfCollection.children));
			const carouselWrapperInnerHTML = this.generateCarouselTemplate(firstChildOfCollection.getAttribute("class") || "", itemsHtml, collection.getAttribute("data-cmpt-carousel") || "");

			const carouselWrapper = this.createCarouselComponentWrapperElement();
			carouselWrapper.innerHTML = carouselWrapperInnerHTML;
			collection.innerHTML = carouselWrapper.outerHTML;
		});
		return collections;
	}

	// tested
	private getCollectionItemsHtml(items: Element[]) {
		const collectionItemsHtml = items.reduce((pre, cur) => {
			return (pre += this.generateItemTemplate(cur.outerHTML));
		}, "");
		return collectionItemsHtml;
	}

	// tested
	private generateItemTemplate(html: string) {
		return `<div class="splide__slide"><div class="splide__slide__container">${html}</div></div>`;
	}

	// tested
	private generateCarouselTemplate(slideListDataAttrOfCollectionClasses: string, itemsHtml: string, carouselType: string) {
		return `
			<div class="carousel__outer-wrapper">
				<div class="carousel__inner-wrapper">
					<div class="splide">
						<div class="splide__track">
							<div class="splide__list" data-collection-list-classes="${slideListDataAttrOfCollectionClasses || ""}">${itemsHtml}</div>
						</div>
						<div class="splide__arrows">
							${
								carouselType === "contactUs" || carouselType === "mediaGallery" || carouselType === "accreditations"
									? ` 
							<button class="splide__arrow--prev" type="button" aria-label="Show previous item">
								<svg xmlns="http://www.w3.org/2000/svg" width="25" height="14" viewBox="0 0 25 14" fill="none">
									<path d="M7.918 13.7953C7.70359 14.0682 7.27478 14.0682 7.00677 13.7953L0.789017 7.46394C0.52101 7.19103 0.52101 6.80897 0.789017 6.53606L7.00677 0.204681C7.27478 -0.0682236 7.70359 -0.0682235 7.918 0.204681L8.34681 0.586747C8.56121 0.859651 8.56121 1.24172 8.34681 1.51462L3.84429 6.09942L23.9448 6.09942C24.32 6.09942 24.588 6.37232 24.588 6.75439L24.588 7.3002C24.588 7.62768 24.32 7.95517 23.9448 7.95517L3.84429 7.95517L8.34681 12.4854C8.56121 12.7583 8.56121 13.1403 8.34681 13.4133L7.918 13.7953Z" fill="#0F1422"/>
								</svg>
							</button>
							<button class="splide__toggle-play is-playing" type="button">
								<svg class="splide__pause--is-paused" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="1.19922" y="1.19922" width="5.66957" height="17.6" fill="#FCFBF8" stroke="#FCFBF8" stroke-width="2"></rect><rect x="13.1289" y="1.19922" width="5.66956" height="17.6" fill="#FCFBF8" stroke="#FCFBF8" stroke-width="2"></rect></svg>
								<svg class="splide__pause--is-playing" width="20" height="22" viewBox="0 0 20 22" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1.69961 1.82052L17.5996 11.0004L1.69961 20.1803L1.69961 1.82052Z" fill="#FCFBF8" stroke="#FCFBF8" stroke-width="2"></path></svg>
							</button>
							<button class="splide__arrow--next" type="button" aria-label="Show next item">
								<svg xmlns="http://www.w3.org/2000/svg" width="25" height="14" viewBox="0 0 25 14" fill="none">
								<path d="M17.434 0.204681C17.6484 -0.0682232 18.0772 -0.0682231 18.3452 0.204681L24.563 6.53606C24.831 6.80897 24.831 7.19104 24.563 7.46394L18.3452 13.7953C18.0772 14.0682 17.6484 14.0682 17.434 13.7953L17.0052 13.4133C16.7908 13.1404 16.7908 12.7583 17.0052 12.4854L21.5077 7.90059L1.40719 7.90058C1.03198 7.90058 0.763976 7.62768 0.763976 7.24561L0.763976 6.6998C0.763976 6.37232 1.03198 6.04483 1.40719 6.04483L21.5077 6.04484L17.0052 1.51462C16.7908 1.24172 16.7908 0.859652 17.0052 0.586747L17.434 0.204681Z" fill="#0F1422"/>
								</svg>
							</button>`
									: `
							<button class="splide__arrow--prev" type="button" aria-label="Show previous item">
								<svg width="26" height="26" viewBox="0 0 26 26" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M18.3004 3.82052L2.40039 13.0004L18.3004 22.1803L18.3004 3.82052Z" fill="#FCFBF8" stroke="#FCFBF8" stroke-width="2"></path></svg>
							</button>
							<button class="splide__toggle-play is-playing" type="button">
								<svg class="splide__pause--is-paused" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="1.19922" y="1.19922" width="5.66957" height="17.6" fill="#FCFBF8" stroke="#FCFBF8" stroke-width="2"></rect><rect x="13.1289" y="1.19922" width="5.66956" height="17.6" fill="#FCFBF8" stroke="#FCFBF8" stroke-width="2"></rect></svg>
								<svg class="splide__pause--is-playing" width="20" height="22" viewBox="0 0 20 22" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1.69961 1.82052L17.5996 11.0004L1.69961 20.1803L1.69961 1.82052Z" fill="#FCFBF8" stroke="#FCFBF8" stroke-width="2"></path></svg>
							</button>
							<button class="splide__arrow--next" type="button" aria-label="Show next item">
								<svg width="20" height="22" viewBox="0 0 20 22" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1.69961 1.82052L17.5996 11.0004L1.69961 20.1803L1.69961 1.82052Z" fill="#FCFBF8" stroke="#FCFBF8" stroke-width="2"></path></svg>
							</button>`
							}
						</div>
					</div>
				</div>
			</div>
		`;
	}

	// tested
	private createCarouselComponentWrapperElement() {
		const el = this.documentHelper.createElement("div");
		el.classList.add("cmpt");
		el.setAttribute("data-cmpt", "carousel");
		return el;
	}

	// tested
	private handleRemountExtension(splide: Splide) {
		return {
			mount: () => {
				this.handleCarouselMounted(splide);
				splide.on("destroy", () => {
					this.handleCarouselDestroyed(splide);
				});
			},
		};
	}

	// tested
	private handleCarouselMounted(splide: Splide) {
		splide.root.parentElement?.parentElement?.parentElement?.classList.add("is-active");
		const splideListEl = splide.root.querySelector<HTMLButtonElement>(".splide__list") as HTMLButtonElement;
		splideListEl.setAttribute("class", "splide__list");
		this.disablePaginationDots(splide);
		this.setupEventListeners(splide);
	}

	// tested
	private handleCarouselDestroyed(splide: Splide) {
		splide.root.parentElement?.parentElement?.parentElement?.classList.remove("is-active");
		const splideListEl = splide.root.querySelector<HTMLButtonElement>(".splide__list") as HTMLButtonElement;
		splideListEl.setAttribute("class", `splide__list ${splideListEl.getAttribute("data-collection-list-classes")}`);

		const togglePlayButton = splide.root.querySelector<HTMLButtonElement>(".splide__toggle-play") as HTMLButtonElement;
		togglePlayButton.removeAttribute("aria-label");
		togglePlayButton.classList.add("is-playing");
		togglePlayButton.outerHTML = (togglePlayButton.cloneNode(true) as HTMLElement).outerHTML;

		splide.off("autoplay:play");
		splide.off("autoplay:pause");

		splide.root.querySelectorAll<HTMLElement>("[data-original-tabindex]").forEach((x) => this.reApplyOriginalTabIndexToDom(x));
	}

	// tested
	private setupEventListeners(splide: Splide) {
		const togglePlayButton = splide.root.querySelector<HTMLButtonElement>(".splide__toggle-play") as HTMLButtonElement;
		togglePlayButton.addEventListener("click", () => this.handleAutoPlayToggle(splide));
		splide.on("autoplay:play", () => this.handleAutoPlayActivate(togglePlayButton));
		splide.on("autoplay:pause", () => this.handleAutoPlayNotActive(togglePlayButton));
		splide.on("pagination:mounted", () => this.disablePaginationDots(splide));
		splide.on("active", (slide: SlideComponent) => setTimeout(() => slide.slide.removeAttribute("tabindex"), 200));
	}

	// tested
	private handleAutoPlayActivate(togglePlayButton: HTMLElement) {
		togglePlayButton.setAttribute("aria-label", "Start autoplay");
		togglePlayButton.classList.add("is-playing");
	}

	// tested
	private handleAutoPlayNotActive(togglePlayButton: HTMLElement) {
		togglePlayButton.setAttribute("aria-label", "Pause autoplay");
		togglePlayButton.classList.remove("is-playing");
	}

	// tested
	private handleAutoPlayToggle(splide: Splide) {
		const autoplay = splide.Components.Autoplay;
		if (autoplay.isPaused()) {
			autoplay.play();
		} else {
			autoplay.pause();
		}
	}

	// tested
	private getCarouselOptions(carouselType = "") {
		switch (carouselType) {
			case "products":
				return Object.assign({}, this.defaultOptions, {
					breakpoints: {
						1200: {
							destroy: true,
						},
						768: {
							perPage: 2,
							gap: "20px",
						},
						480: {
							perPage: 1,
						},
					},
				});
			case "contactUs":
				return Object.assign({}, this.defaultOptions, {
					breakpoints: {
						1200: {
							destroy: true,
						},
					},
					perPage: 1,
					gap: "20px",
					autoplay: false,
				});
			case "accreditations":
				return Object.assign({}, this.defaultOptions, {
					breakpoints: {
						1200: {
							destroy: true,
						},
					},
					perPage: 1,
					gap: "20px",
					autoplay: false,
					padding: "1rem",
				});
			case "mediaGallery":
				return Object.assign({}, this.defaultOptions, {
					breakpoints: {
						1200: {
							destroy: true,
						},
					},
					perPage: 1,
					gap: "16px",
					autoplay: false,
					padding: "1rem",
				});
			default:
				return this.defaultOptions;
		}
	}

	// tested
	private storeOriginalTabIndex(el: HTMLElement) {
		el.setAttribute("data-original-tabindex", el.getAttribute("tabindex") || "0");
	}

	// tested
	private reApplyOriginalTabIndexToDom(el: HTMLElement) {
		el.setAttribute("tabindex", el.getAttribute("data-original-tabindex") || "0");
	}

	// tested
	private disablePaginationDots(splide: Splide) {
		splide.root.querySelectorAll<HTMLButtonElement>("button.splide__pagination__page").forEach((x) => {
			x.setAttribute("aria-disabled", "true");
			x.setAttribute("disabled", "");
		});
	}
}
